import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment-timezone';

import Strings from '../../../../Strings';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import DropdownMenu from '../../../../components/DropdownMenu/DropdownMenu';
import { DATE_FORMAT_MONTH_NAME } from '../../../../constants';
import { getMedsType } from '../../../../utils';
import { MEDICATION_ROW_KINDS } from '../../../../utils/rpmPatientFields';
import { ORG_FEATURES, orgFeatureEnabled } from '../../../../utils/userPermissions';
import { renderPrescriber } from '../PatientInfo/MedicalHistory';
import { renderInsuranceInfo } from '../../../../modals/OnboardPatientWizard/MedicalHistoryPage';
import {
  getLocalDate,
  getMilestoneTexts,
  getReminderTexts,
  getScheduleProgramName,
  getTakeWindowText,
  getTimesValue,
} from './scheduleDetails';

export const getMedicationRowStatus = row => ({
  [MEDICATION_ROW_KINDS.scheduled]: Strings.patient_medications.statusScheduled,
  [MEDICATION_ROW_KINDS.scheduleOnly]: Strings.patient_medications.statusNotInMedicationList,
  [MEDICATION_ROW_KINDS.medicationOnly]: Strings.patient_medications.statusNotScheduled,
}[row.kind]);

// Clicks inside a cell's controls (including the cap menu, rendered in a portal) must not select the row.
const stopRowClick = e => e.stopPropagation();

function ActionIcon({ icon, label, onClick, testId }) {
  return (
    <div className="icon-container" data-tooltip-content={label} data-tooltip-id="tooltip">
      <div
        className={`icon ${icon}`}
        onClick={onClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={label}
        data-testid={testId}
      />
    </div>
  );
}

ActionIcon.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

// The assigned cap with its detach action, or the attach menu of the patient's caps without a schedule.
function ScheduleCapCell({ schedule, devices, canEdit, onAttachCap, onDetachCap }) {
  if (schedule?.capId) {
    return (
      <div className="medication-cap" onClick={stopRowClick}>
        <div>{schedule.capId}</div>
        {canEdit && (
          <ActionIcon
            icon="delete"
            label={Strings.patient_medications.detachCap}
            onClick={() => onDetachCap(schedule)}
            testId={`delete-cap-button[${schedule.capId}]`}
          />
        )}
      </div>
    );
  }
  if (!schedule || !canEdit || !devices) {
    return '-';
  }
  return (
    <div className="addMenu" onClick={stopRowClick}>
      <DropdownMenu
        icon="plus"
        title={Strings.patient_medications.attachCap}
        options={
          devices.length > 0
            ? devices.map(device => ({ label: device, onSelect: () => onAttachCap(schedule, device) }))
            : [{ label: Strings.noCapAvailable, onSelect: () => {} }]
        }
        buttonTestId={`add-cap-button[${schedule.medicationName}]`}
      />
    </div>
  );
}

ScheduleCapCell.propTypes = {
  schedule: PropTypes.object,
  devices: PropTypes.array,
  canEdit: PropTypes.bool,
  onAttachCap: PropTypes.func,
  onDetachCap: PropTypes.func,
};

// Row actions shown on hover, like the patient list: schedule rows can be edited or removed, medication-only rows
// get a schedule (canEdit); rows with a patient medications list entry open it in Medical History or remove it from
// there (canEditMedicalHistory). Test ids: table-button_<testId>_<row index>.
const getButtons = ({
  canEdit,
  canEditMedicalHistory,
  onEditSchedule,
  onRemoveSchedule,
  onAddSchedule,
  onEditMedicalHistory,
  onRemoveFromMedicalHistory,
}) => [
  ...(canEdit ? [
    {
      icon: 'edit',
      text: Strings.patient_medications.editSchedule,
      testId: 'edit-schedule',
      visible: (_, row) => !!row.schedule,
      onClick: (_, row) => onEditSchedule(row.schedule),
    },
    {
      icon: 'delete',
      text: Strings.patient_medications.removeSchedule,
      testId: 'delete-schedule',
      visible: (_, row) => !!row.schedule,
      onClick: (_, row) => onRemoveSchedule(row.schedule),
    },
    {
      icon: 'plus',
      text: Strings.patient_medications.addSchedule,
      testId: 'add-schedule',
      visible: (_, row) => !row.schedule,
      onClick: (_, row) => onAddSchedule(row.patientMedication),
    },
  ] : []),
  ...(canEditMedicalHistory ? [
    {
      icon: 'register',
      text: Strings.patient_medications.editInMedicalHistory,
      testId: 'edit-medical-history',
      visible: (_, row) => !!row.patientMedication,
      onClick: (_, row) => onEditMedicalHistory(row.patientMedication),
    },
    {
      icon: 'unregister',
      text: Strings.patient_medications.removeFromMedicalHistory,
      testId: 'remove-medical-history',
      visible: (_, row) => !!row.patientMedication,
      onClick: (_, row) => onRemoveFromMedicalHistory(row.patientMedication, row.schedule),
    },
  ] : []),
];

// Text in a div, not a span (the global span style in layout.scss would grey it out), with the whole value in the
// portal tooltip for when the column cuts it.
function CellText({ text, children }) {
  return (
    <div className="medication-cell-text" data-tooltip-content={text} data-tooltip-id="tooltip">
      {children || text}
    </div>
  );
}

CellText.propTypes = {
  text: PropTypes.string,
  children: PropTypes.node,
};

const textCell = info => <CellText text={info.getValue() || '-'} />;

const textColumn = (id, header, size) => columnHelper.accessor(id, { header, size, cell: textCell });

// Every schedule detail and every patient medications list entry field, one row per medication. Rows show what they
// have: schedule columns stay '-' for medication-only rows and list entry columns for schedules not in the list.
const getColumns = ({ devices, canEdit, onAttachCap, onDetachCap }) => [
  columnHelper.accessor('name', {
    header: Strings.medication,
    size: 300,
    cell: info => <div className="medication-name" data-testid={`medication-row-[${info.getValue()}]`}>{info.getValue()}</div>,
  }),
  textColumn('ndc', 'NDC', 140),
  textColumn('status', Strings.patient_medications.status, 190),
  textColumn('scheduleType', Strings.patient_medications.schedule, 150),
  columnHelper.accessor('times', {
    header: Strings.patient_medications.times,
    size: 280,
    cell: info => {
      const { schedule } = info.row.original;
      if (!schedule) {
        return '-';
      }
      const { display, tooltip } = getTimesValue(schedule, true);
      return <CellText text={tooltip}>{display}</CellText>;
    },
  }),
  textColumn('startDate', Strings.patient_medications.startDate, 140),
  textColumn('endDate', Strings.patient_medications.endDate, 140),
  textColumn('createdEdited', `${Strings.patient_medications.createdOn} / ${Strings.patient_medications.editedOn}`, 240),
  columnHelper.accessor('capId', {
    header: Strings.patient_medications.assignedCap,
    size: 200,
    cell: info => (
      <ScheduleCapCell
        schedule={info.row.original.schedule}
        devices={devices}
        canEdit={canEdit}
        onAttachCap={onAttachCap}
        onDetachCap={onDetachCap}
      />
    ),
  }),
  textColumn('programName', Strings.program, 200),
  columnHelper.accessor('adherence', {
    header: Strings.adherence,
    size: 120,
    cell: info => (info.getValue() ? `${info.getValue()}%` : '-'),
  }),
  textColumn('reminders', Strings.reminders, 300),
  textColumn('administrationWindow', Strings.administrationWindow, 200),
  ...(orgFeatureEnabled(ORG_FEATURES.engagement_messages_enabled)
    ? [textColumn('engagementMessages', Strings.capPatient.engagementMessages, 300)]
    : []),
  textColumn('sig', Strings.patient_medications.sig, 200),
  textColumn('notes', Strings.patient_medications.medicationNotes, 200),
  textColumn('prescriber', Strings.patient_medications.prescriber, 180),
  textColumn('fillDate', Strings.patient_medications.fillDate, 140),
  textColumn('rxNumber', Strings.patient_medications.rxNumber, 140),
  textColumn('insurance', Strings.capPatient.insurance, 200),
  textColumn('conditions', Strings.condition, 200),
];

// The schedule's created date, with the edited one when it was edited.
const getCreatedEditedText = schedule => {
  const createdOn = getLocalDate(schedule.createdAt, schedule.scheduleTimezone);
  return schedule.editedAt ? `${createdOn} / ${getLocalDate(schedule.editedAt, schedule.scheduleTimezone)}` : createdOn;
};

const getRowData = (row, patientEnrollments, patientConditions) => {
  const { schedule, patientMedication } = row;
  const scheduleData = schedule
    ? {
      scheduleType: getMedsType(schedule.format?.type),
      times: getTimesValue(schedule, true).tooltip,
      startDate: getLocalDate(schedule.startDate, schedule.scheduleTimezone),
      endDate: getLocalDate(schedule.endDate, schedule.scheduleTimezone),
      createdEdited: getCreatedEditedText(schedule),
      capId: schedule.capId || '',
      programName: getScheduleProgramName(schedule, patientEnrollments),
      adherence: schedule.analytics?.adherence ?? null,
      reminders: getReminderTexts(schedule).join(', '),
      administrationWindow: getTakeWindowText(schedule),
      engagementMessages: getMilestoneTexts(schedule).join(', '),
    }
    : { capId: '', adherence: null };
  const conditionTitles = (patientMedication?.conditions || []).map(id => patientConditions?.[id]?.title).filter(Boolean);
  return {
    ...row,
    status: getMedicationRowStatus(row),
    ...scheduleData,
    // The schedule's own SIG and notes; the list entry's when there is no schedule or it has none.
    sig: schedule?.format?.sig || patientMedication?.sig || '',
    notes: schedule?.format?.notes || patientMedication?.notes || '',
    prescriber: patientMedication?.prescriber ? renderPrescriber(patientMedication.prescriber) : '',
    fillDate: patientMedication?.fillDate ? moment(patientMedication.fillDate).format(DATE_FORMAT_MONTH_NAME) : '',
    rxNumber: patientMedication?.rxNumber || '',
    insurance: patientMedication?.insurance ? renderInsuranceInfo(patientMedication.insurance) : '',
    conditions: conditionTitles.join(', '),
  };
};

function MedicationsTable(props) {
  const { rows, devices, patientEnrollments, patientConditions, canEdit, canEditMedicalHistory, isLoading } = props;
  const { selectedRowKey, onRowClick, onEditSchedule, onRemoveSchedule, onAddSchedule, onAttachCap, onDetachCap } = props;
  const { onEditMedicalHistory, onRemoveFromMedicalHistory } = props;

  const data = React.useMemo(
    () => rows.map(row => getRowData(row, patientEnrollments, patientConditions)),
    [rows, patientEnrollments, patientConditions],
  );

  const columns = React.useMemo(
    () => getColumns({ devices, canEdit, onAttachCap, onDetachCap }),
    [devices, canEdit, onAttachCap, onDetachCap],
  );
  const buttons = React.useMemo(
    () => getButtons({
      canEdit,
      canEditMedicalHistory,
      onEditSchedule,
      onRemoveSchedule,
      onAddSchedule,
      onEditMedicalHistory,
      onRemoveFromMedicalHistory,
    }),
    [canEdit, canEditMedicalHistory, onEditSchedule, onRemoveSchedule, onAddSchedule, onEditMedicalHistory, onRemoveFromMedicalHistory],
  );

  return (
    <Table3
      name="patient-medications-unified"
      data={data}
      columns={columns}
      isLoading={isLoading}
      onRowClick={onRowClick}
      buttons={buttons}
      pinFirstColumn
      getRowClassName={row => (row.key === selectedRowKey ? 'selected' : '')}
      noPagination
    />
  );
}

MedicationsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  devices: PropTypes.array,
  patientEnrollments: PropTypes.array,
  patientConditions: PropTypes.object,
  canEdit: PropTypes.bool,
  canEditMedicalHistory: PropTypes.bool,
  isLoading: PropTypes.bool,
  selectedRowKey: PropTypes.string,
  onRowClick: PropTypes.func,
  onEditSchedule: PropTypes.func,
  onRemoveSchedule: PropTypes.func,
  onAddSchedule: PropTypes.func,
  onAttachCap: PropTypes.func,
  onDetachCap: PropTypes.func,
  onEditMedicalHistory: PropTypes.func,
  onRemoveFromMedicalHistory: PropTypes.func,
};

export default MedicationsTable;
